import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material'

import { formatQuantity, toSafeNumber } from '../../../../helpers/utils'
import { UI_TEXT_EM_DASH } from '../../../../constants/uiText'
import { REFUND_APPROVAL_TEXT } from '../../../constants/refundApprovalText'
import { getRefundLineOriginalQuantity, getRefundLineStatusLabel } from '../../../constants/refundLineStatus'
import { getRefundLineListKey } from '../../../helpers/refundApprovalUtils'
import {
  UI_COLOR_BORDER_HAIRLINE,
  UI_COLOR_BUTTON_MUTED,
  UI_COLOR_PRIMARY,
  UI_COLOR_PRIMARY_HOVER,
  UI_COLOR_PRIMARY_SOFT,
  UI_COLOR_TEXT_INK,
  UI_COLOR_TEXT_TERTIARY
} from '../../../../constants/uiColors'

const RefundScanPackagingChoiceDialog = ({ open, lines = [], onSelect, onClose }) => {
  const [selectedKey, setSelectedKey] = useState('')
  const productName = lines[0]?.product_name || lines[0]?.product_db_ref || ''
  const selectedLine = lines.find((line, index) => getRefundLineListKey(line, index) === selectedKey)

  useEffect(() => {
    if (!open) {
      setSelectedKey('')
      return
    }

    setSelectedKey('')
  }, [open, lines])

  const handleConfirm = () => {
    if (!selectedLine) return
    onSelect(selectedLine)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      transitionDuration={0}
      aria-labelledby="refund-packaging-choice-title"
    >
      <DialogTitle
        id="refund-packaging-choice-title"
        sx={{ fontSize: 20, fontWeight: 700, px: 3, pt: 2.5, pb: 1 }}
      >
        {REFUND_APPROVAL_TEXT.packagingChoiceTitle}
      </DialogTitle>
      <DialogContent sx={{ px: 3, pb: 1 }}>
        <Typography sx={{ mb: 2, fontSize: 14, color: UI_COLOR_TEXT_TERTIARY }}>
          {REFUND_APPROVAL_TEXT.packagingChoiceSubtitle}
        </Typography>
        {productName ? (
          <Typography sx={{ mb: 1.5, fontSize: 14, fontWeight: 700, color: UI_COLOR_TEXT_INK }}>
            {productName}
          </Typography>
        ) : null}
        <RadioGroup
          value={selectedKey}
          onChange={(event) => setSelectedKey(event.target.value)}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {lines.map((line, index) => {
              const optionKey = getRefundLineListKey(line, index)
              const unit = line.price_unit || line.unit_of_sale || REFUND_APPROVAL_TEXT.defaultUnit
              const originalQty = toSafeNumber(getRefundLineOriginalQuantity(line))
              const motive = line.motive || UI_TEXT_EM_DASH
              const statusLabel = getRefundLineStatusLabel(line)
              const isSelected = optionKey === selectedKey

              return (
                <FormControlLabel
                  key={optionKey}
                  value={optionKey}
                  control={
                    <Radio
                      inputProps={{
                        'aria-label': `${REFUND_APPROVAL_TEXT.selectPackagingAria} ${unit}`
                      }}
                      sx={{
                        color: UI_COLOR_PRIMARY,
                        '&.Mui-checked': { color: UI_COLOR_PRIMARY }
                      }}
                    />
                  }
                  sx={{
                    m: 0,
                    alignItems: 'flex-start',
                    px: 1.5,
                    py: 1.15,
                    border: `1px solid ${isSelected ? UI_COLOR_PRIMARY : UI_COLOR_BORDER_HAIRLINE}`,
                    borderRadius: 1,
                    backgroundColor: isSelected ? UI_COLOR_PRIMARY_SOFT : 'transparent',
                    '&:hover': {
                      borderColor: UI_COLOR_PRIMARY,
                      backgroundColor: UI_COLOR_PRIMARY_SOFT
                    }
                  }}
                  label={
                    <Box sx={{ pt: 0.35 }}>
                      <Typography sx={{ fontSize: 15, fontWeight: 800, color: UI_COLOR_PRIMARY }}>
                        {REFUND_APPROVAL_TEXT.packagingChoicePackaging}: {unit}
                      </Typography>
                      <Typography sx={{ mt: 0.35, fontSize: 13, color: UI_COLOR_TEXT_TERTIARY }}>
                        {REFUND_APPROVAL_TEXT.packagingChoiceMotive}: {motive}
                        {' · '}
                        {REFUND_APPROVAL_TEXT.packagingChoiceOriginalQty}: {formatQuantity(originalQty)}
                        {' · '}
                        {REFUND_APPROVAL_TEXT.packagingChoiceStatus}: {statusLabel}
                      </Typography>
                    </Box>
                  }
                />
              )
            })}
          </Box>
        </RadioGroup>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.25, pt: 1, gap: 1 }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={onClose}
          sx={{
            color: UI_COLOR_BUTTON_MUTED,
            borderColor: UI_COLOR_BUTTON_MUTED,
            '&:hover': {
              borderColor: UI_COLOR_BUTTON_MUTED,
              bgcolor: 'transparent'
            }
          }}
        >
          {REFUND_APPROVAL_TEXT.cancel}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!selectedLine}
          disableElevation
          sx={{
            bgcolor: UI_COLOR_PRIMARY,
            '&:hover': { bgcolor: UI_COLOR_PRIMARY_HOVER },
            '&.Mui-disabled': { bgcolor: UI_COLOR_PRIMARY, opacity: 0.45, color: '#fff' }
          }}
        >
          {REFUND_APPROVAL_TEXT.packagingChoiceConfirm}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RefundScanPackagingChoiceDialog
